import { useEffect } from 'react';
import Api from 'Vendor/Api';
import { FONT_WEIGHT } from 'Vendor/Utils/Constants';
import { normalizeFontWeight } from 'Vendor/Utils/Fonts';
import { Font } from 'Vendor/Utils/StudioTypes';

const fontCache = new Map<string, FontFace>();

interface UseFontLoaderParams {
    family?: string | null;
    weight?: number | string;
    style?: string;
    platformFonts?: Font[];
}

const useFontLoader = ({
    family,
    weight,
    style = 'normal',
    platformFonts = [],
}: UseFontLoaderParams) => {
    useEffect(() => {
        if (!family || !platformFonts.length) return;

        const font = platformFonts.find(f => f.family === family);
        if (!font?.variants?.length) return;

        const normalizedWeight = normalizeFontWeight(weight);

        const variant =
            font.variants.find(v =>
                String(v.weight) === normalizedWeight &&
                (v.style || 'normal') === style
            ) ||
            font.variants.find(v => String(v.weight) === String(FONT_WEIGHT.REGULAR)) ||
            font.variants[0];

        if (!variant?.file) return;

        const loadFont = async () => {
            try {
                if (fontCache.has(variant.file)) {
                    document.fonts.add(fontCache.get(variant.file)!);
                    return;
                }

                const res = await Api.get(`/cdn/font/file/${variant.file}`, {
                    responseType: 'arraybuffer',
                });

                const blob = new Blob([res.data], { type: 'font/woff2' });
                const fontUrl = URL.createObjectURL(blob);

                const fontFace = new FontFace(
                    font.family,
                    `url(${fontUrl})`,
                    {
                        weight: variant.weight?.toString(),
                        style: variant.style || 'normal',
                    }
                );

                const loadedFont = await fontFace.load();

                document.fonts.add(loadedFont);
                fontCache.set(variant.file, loadedFont);

                URL.revokeObjectURL(fontUrl);
            } catch (err) {
                console.error(`Failed to load font variant: ${variant.file}`, err);
            }
        };

        loadFont();
    }, [family, weight, style, platformFonts]);
};

export default useFontLoader;
